/**
 * Small building blocks for the Shop Drawings page (discipline chips, "No ID" tags, avatars,
 * toolbar search, filter chips with a picker, the table footer + pager, callouts, group rows and
 * the floating bulk bar). They sit on the app foundation (FilterChip, StatusPill, Button, tokens).
 */
import { forwardRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, CircleHelp, Minus, X } from 'lucide-react';
import { SearchField as UiSearchField } from '@/components/ui/search-field';
import { ChipSelect as UiChipSelect } from '@/components/ui/chip-select';
import { Pager as UiPager } from '@/components/ui/pager';
import { Segmented } from '@/components/ui/segmented';
import { StatusPill } from '@/components/ui/status-pill';
import { cn } from '@/lib/utils';
import { formatDays, formatNumber } from '@/lib/format';
import { TONE_TEXT_CLASS } from '@/lib/status';
import { ageToneOf, blockerMeta, displayPerson, personColor, personInitials, realDrawingId } from './model';

/* ------------------------------------------------------------------ table shell */

export const tableClass = 'w-full border-separate border-spacing-0 text-[13px] text-ink';
export const thClass = 'h-9 whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left text-[11.5px] font-bold tracking-[0.02em] text-ink-3';
export const tdClass = 'border-b border-line-2 px-3 py-2 align-middle';
export const subClass = 'mt-0.5 block text-[11.5px] text-ink-3';

/* ------------------------------------------------------------------ identity bits */

// Filled, high-contrast discipline chips (white text ≥ 4.5:1 on every fill).
const DISCIPLINE_FILLS = {
  BMS: '#3d3da3',
  LCS: '#15803d',
  HA: '#0f766e',
  HAS: '#0f766e',
  KNX: '#0f766e',
  GRMS: '#be185d',
  EMS: '#b45309',
  ELV: '#0369a1',
  FIRE: '#b91c1c',
  RPC_CONNECTIONS: '#4338ca',
  OTHER: '#5f6187',
};
const DISCIPLINE_SHORT = { RPC_CONNECTIONS: 'RPC' };

export function DisciplineChip({ disc, className }) {
  const value = String(disc || 'OTHER').toUpperCase();
  const label = DISCIPLINE_SHORT[value] || value.replace(/_/g, ' ');
  return (
    <span
      className={cn('inline-grid h-[22px] min-w-[44px] place-items-center rounded-md px-1.5 text-[10.5px] font-bold leading-none tracking-[0.04em] text-white', className)}
      style={{ background: DISCIPLINE_FILLS[value] || DISCIPLINE_FILLS.OTHER }}
      title={value === 'HA' ? 'Home automation' : undefined}
      data-testid="discipline-chip"
    >
      {label}
    </span>
  );
}

/** Amber "No ID" tag shown instead of placeholder Drawing IDs ('-', 'NO ID', 'NOID', 'NONE', 'NO_ID'). */
export function NoIdTag({ className }) {
  return (
    <span
      className={cn('inline-flex h-[18px] items-center rounded-[5px] border border-[color:var(--status-amber-bg)] bg-status-amber-bg px-1.5 text-[10.5px] font-bold text-status-amber-ink', className)}
      title="This drawing has no Drawing ID yet"
    >
      No ID
    </span>
  );
}

/**
 * A real Drawing ID in mono, or the "No ID" tag. `keepEnd`: when it does not fit, cut the start instead of the end
 * ("…-PL-LI-22013"), where IDs of one transmittal differ. `wrap`: break onto more lines instead of cutting (drawer).
 */
export function DrawingIdText({ value, className, keepEnd = false, wrap = false }) {
  const id = realDrawingId(value);
  // The tag never shrinks: squeezed, its text would run under what follows it.
  if (!id) return <NoIdTag className={cn(className, 'shrink-0')} />;
  if (wrap) return <span className={cn('block whitespace-normal break-all font-mono text-[11.5px] text-ink-2', className)} title={id}>{id}</span>;
  if (keepEnd) {
    // An RTL box puts the ellipsis at the start; the ID itself stays left-to-right inside an isolate.
    return (
      <span className={cn('truncate font-mono text-[11.5px] text-ink-2', className)} dir="rtl" title={id}>
        <bdi dir="ltr">{id}</bdi>
      </span>
    );
  }
  return <span className={cn('truncate font-mono text-[11.5px] text-ink-2', className)} title={id}>{id}</span>;
}

/**
 * The optional bits of a row's sub-line (type, revision, PDF, internal SD number). When the line is too short they
 * drop out whole, from the end — never as a cut "· …" stub — and all of them go before the Drawing ID before them
 * loses a single character. Must be the last item of a `gap-1.5` flex line.
 */
export function SubLineExtras({ children, className }) {
  // Flex basis 0 + grow: the box only ever gets the room left over once everything before it has its full width, so
  // it never takes width from the ID (a shrink factor only splits the overflow, and the ID was still cut by a few px).
  // A one-line box that hides its second line: a part that does not fit wraps out of sight. The empty first item lets
  // even the first part wrap away (a lone first item would be cut instead). -ml-1.5 cancels the line's gap before the
  // box (each part brings its own margin), so with every part gone the ID gets those 6px too.
  return (
    <span className={cn('-ml-1.5 flex h-[18px] min-w-0 grow basis-0 flex-wrap items-center overflow-hidden leading-[18px] [&>*]:shrink-0 [&>*:nth-child(2)]:ml-1.5 [&>*:nth-child(n+3)]:ml-1.5', className)}>
      <span className="h-[18px] w-0" aria-hidden="true" />
      {children}
    </span>
  );
}

/** "· Floor Plan" inside SubLineExtras (the dot is decoration; screen readers hear the text). */
export function SubLinePart({ children, className, title }) {
  return (
    <span className={className} title={title}>
      <span aria-hidden="true">· </span>
      <span>{children}</span>
    </span>
  );
}

export function PersonAvatar({ name, size = 22, className }) {
  return (
    <span
      className={cn('inline-grid shrink-0 place-items-center rounded-full font-bold text-white', className)}
      style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.43)), background: personColor(name) }}
      aria-hidden="true"
    >
      {personInitials(name)}
    </span>
  );
}

/** Avatar + name; muted "Unassigned" when empty. */
export function PersonChip({ name, prefix, className }) {
  const label = displayPerson(name);
  if (!label) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-[12.5px] text-ink-3', className)}>
        {prefix ? <span>{prefix}</span> : null}
        <span className="italic">Unassigned</span>
      </span>
    );
  }
  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1.5 text-[12.5px] text-ink', className)}>
      {prefix ? <span className="text-ink-3">{prefix}</span> : null}
      <PersonAvatar name={label} />
      <span className="truncate font-semibold">{label}</span>
    </span>
  );
}

/** Blocker reason as a square badge: real reason red, "Reason missing" amber with a help icon. */
export function ReasonBadge({ drawing, className }) {
  const meta = blockerMeta(drawing);
  if (!meta.label) return <span className="text-ink-4" aria-label="No blocker">—</span>;
  return (
    <StatusPill
      tone={meta.tone}
      label={meta.label}
      shape="square"
      icon={meta.missing ? CircleHelp : undefined}
      className={className}
      data-testid={meta.missing ? 'reason-missing' : undefined}
    />
  );
}

/** '86 d' coloured by age (red > 90 d, amber 31–90 d). */
export function AgeText({ days, suffix = '', className }) {
  if (days === null || days === undefined) return <span className={cn('text-ink-4', className)}>—</span>;
  const tone = ageToneOf(days);
  return (
    <span className={cn('font-semibold tabular-nums', tone ? TONE_TEXT_CLASS[tone] : 'text-ink-2', className)}>
      {formatDays(days)}{suffix}
    </span>
  );
}

export function CountPill({ tone = 'gray', className, children }) {
  return (
    <span className={cn(`tone-${tone}`, 'inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-[color:var(--tone-bg)] px-1.5 text-[11px] font-bold tabular-nums text-[color:var(--tone-ink)]', className)}>
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ toolbar */

/** Toolbar search; "/" focuses it from anywhere on the page. */
/** Toolbar search (components/ui/search-field); "/" focuses the one on the visible tab. */
export const SearchField = forwardRef(function SearchField(props, ref) {
  return <UiSearchField ref={ref} onlyWhenVisible {...props} />;
});

/**
 * Filter chip with a picker: dashed "Project ⌄" when unset, "Project: NOBU ×" when set
 * (components/ui/chip-select). options: [{ value, label, count }] — searchable when there are more than 8.
 */
export function ChipSelect(props) {
  return <UiChipSelect emptyText="No values" popoverClassName="w-64" {...props} />;
}

/* ------------------------------------------------------------------ selection */

export function SdCheckbox({ className, indeterminate, checked, ...props }) {
  const state = indeterminate ? 'indeterminate' : Boolean(checked);
  return (
    <CheckboxPrimitive.Root
      checked={state}
      className={cn(
        'peer grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border-[1.5px] border-[color:var(--ink-4)] bg-surface text-white transition-colors',
        'hover:border-[color:var(--ink-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:border-brand data-[state=checked]:bg-brand data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator>
        {indeterminate ? <Minus className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> : <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

/* ------------------------------------------------------------------ paging */

/** ‹ 1 … 4 5 6 … 15 › (components/ui/pager, filled current page). */
export function Pager(props) {
  return <UiPager variant="solid" {...props} />;
}

/**
 * Table footer: "Showing 1–50 of 735 …" left; rows-per-page + pager right. `pageSizeLabel` / `pageSizeAriaLabel`
 * name the page size when a page holds something else than rows ("Projects" — All Drawings grouped by project).
 */
export function TableFoot({ left, page, pageCount, onPage, pageSize, pageSizes, onPageSize, pageSizeLabel = 'Rows', pageSizeAriaLabel = 'Rows per page', children, className, testId }) {
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3 border-t border-line-2 bg-surface px-3.5 py-2.5 text-[12.5px] text-ink-3', className)} data-testid={testId}>
      <div className="min-w-0">{left}</div>
      <div className="flex flex-wrap items-center gap-3">
        {children}
        {onPageSize ? (
          <label className="flex items-center gap-1.5">
            <span>{pageSizeLabel}</span>
            <select
              value={pageSize}
              onChange={(event) => onPageSize(Number(event.target.value))}
              aria-label={pageSizeAriaLabel}
              className="h-[30px] rounded-lg border border-line bg-surface px-2 text-[12.5px] font-semibold text-ink-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </label>
        ) : null}
        {onPage ? <Pager page={page} pageCount={pageCount} onPage={onPage} /> : null}
      </div>
    </div>
  );
}

/** Showing 1–50 of 735 */
export function ShowingText({ from, to, total, noun = 'drawings', extra }) {
  const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
  return (
    <span>
      {total ? (
        <>Showing <b className="font-semibold text-ink-2">{fmt(from)}–{fmt(to)}</b> of <b className="font-semibold text-ink-2">{fmt(total)}</b> {noun}</>
      ) : `No ${noun}`}
      {extra ? <> · {extra}</> : null}
    </span>
  );
}

/* ------------------------------------------------------------------ callout */

const CALLOUT_TONES = {
  amber: 'border-[color:rgb(217_119_6/0.28)] bg-status-amber-bg',
  navy: 'border-[color:var(--navy-100)] bg-[color:var(--navy-50)]',
  gray: 'border-line bg-surface',
};

/** Callout line: icon, one or two lines of text, small buttons on the right. */
export function Callout({ tone = 'amber', icon: Icon, title, sub, actions, className, testId }) {
  return (
    <div
      role="status"
      data-testid={testId}
      className={cn('flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-3.5 py-2.5 text-[13px] text-ink', CALLOUT_TONES[tone] || CALLOUT_TONES.gray, className)}
    >
      {Icon ? <Icon className={cn('h-4 w-4 shrink-0 self-start', sub ? 'mt-0.5' : 'mt-0.5', tone === 'amber' ? 'text-status-amber-ink' : 'text-brand-ink')} aria-hidden="true" /> : null}
      <div className="min-w-0 flex-1 leading-5">
        <div>{title}</div>
        {sub ? <div className="mt-px text-[12.5px] leading-[18px] text-ink-2">{sub}</div> : null}
      </div>
      {/* Below sm the buttons go under the text, so the message keeps the full width of a phone. */}
      {actions ? <div className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5 max-sm:w-full max-sm:pl-7">{actions}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ bulk bar */

/**
 * Floating navy bar for a selection. Sticks to the bottom of the window (centred over the content
 * column, whatever the sidebar width) while the table scrolls, and rests under the table at the end.
 */
export function BulkBar({ count, label, onClear, children, testId = 'shop-drawings-bulkbar' }) {
  if (!count) return null;
  return (
    <div className="pointer-events-none sticky bottom-6 z-30 mt-3 flex justify-center px-2" data-testid={testId}>
      <div
        role="toolbar"
        aria-label={`${count} selected`}
        className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1.5 rounded-xl bg-[color:var(--navy)] py-2 pl-3.5 pr-2 text-[13px] font-semibold text-white shadow-overlay"
      >
        <span className="mr-1.5 inline-flex items-center gap-2 whitespace-nowrap">
          <span className="grid h-[22px] min-w-[22px] place-items-center rounded-full bg-white px-1.5 text-[11.5px] font-extrabold text-[color:var(--navy)] tabular-nums" data-testid="bulkbar-count">
            {formatNumber(count, { maxDecimals: 0 })}
          </span>
          <span>{label || 'selected'}</span>
        </span>
        {children}
        {onClear ? (
          <BulkButton onClick={onClear} icon={X} aria-label="Clear selection" title="Clear selection" className="w-[30px] justify-center px-0" />
        ) : null}
      </div>
    </div>
  );
}

export const BulkButton = forwardRef(function BulkButton({ icon: Icon, children, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg bg-white/10 px-2.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 disabled:opacity-50',
        className
      )}
      {...props}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
      {children}
    </button>
  );
});

export const BulkDivider = () => <span className="mx-0.5 h-5 w-px bg-white/20" aria-hidden="true" />;

/* ------------------------------------------------------------------ wave 2: groups + rows */

const SEGMENT_FILLS = {
  navy: 'var(--status-navy)',
  teal: 'var(--status-teal)',
  amber: 'var(--status-amber)',
  blue: 'var(--status-blue)',
  gray: 'var(--status-gray)',
  red: 'var(--status-red)',
  outline: 'var(--line)',
};
const PATTERN_FILLS = {
  hold: 'repeating-linear-gradient(135deg, var(--status-amber) 0 3px, var(--status-amber-bg) 3px 6px)',
  rejected: 'repeating-linear-gradient(135deg, var(--status-red) 0 3px, var(--status-red-bg) 3px 6px)',
};

/** Fill of a pipeline segment (same colours as the pipeline strip). */
export const segmentFill = (segment) => PATTERN_FILLS[segment.pattern] || SEGMENT_FILLS[segment.tone] || SEGMENT_FILLS.gray;

/** 140×6 stacked bar of a project's drawings by pipeline stage (group rows). */
export function MiniStackBar({ segments = [], width = 140, label, className }) {
  const total = segments.reduce((sum, segment) => sum + segment.count, 0);
  return (
    <span
      role="img"
      aria-label={label || segments.map((segment) => `${segment.label} ${segment.count}`).join(', ')}
      className={cn('flex h-1.5 shrink-0 gap-px overflow-hidden rounded-full bg-[color:var(--line-2)]', className)}
      style={{ width }}
      title={segments.map((segment) => `${segment.label}: ${segment.count}`).join(' · ')}
    >
      {total ? segments.map((segment) => (
        <span key={segment.key} className="block h-full" style={{ flexGrow: segment.count, flexBasis: 0, minWidth: 2, background: segmentFill(segment) }} />
      )) : null}
    </span>
  );
}

/** Small discipline chip for group rows. */
export function SmallDisciplineChip({ disc }) {
  return <DisciplineChip disc={disc} className="h-[18px] min-w-[32px] rounded-[5px] px-1 text-[9.5px]" />;
}

/** Group row title: 'LUXOR TOWER #69 · BMS' (the table adds the count pill). */
/** Group row title: "ATS Majlis SAMHA #62 LCS · 15 blocked · since 6 Jul 2026". `count` stays visible on every
    screen; `meta` shows from lg. */
export function GroupTitle({ label, number, noNumberText, discs = [], count, meta, children }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2 font-normal">
      <span className="truncate text-[13px] font-bold text-ink">{label}</span>
      {number ? <span className="shrink-0 font-mono text-[11.5px] font-medium text-ink-3">#{number}</span> : null}
      {!number && noNumberText ? <span className="shrink-0 text-[11.5px] font-medium text-ink-3">{noNumberText}</span> : null}
      {discs.length ? (
        <span className="inline-flex shrink-0 items-center gap-1">
          {discs.slice(0, 4).map((disc) => <SmallDisciplineChip key={disc} disc={disc} />)}
        </span>
      ) : null}
      {count ? <span className="shrink-0 whitespace-nowrap text-[12px] font-semibold text-ink-2" data-testid="group-title-count">{count}</span> : null}
      {meta ? <span className="hidden shrink-0 text-[12px] font-medium text-ink-3 lg:inline">{meta}</span> : null}
      {children}
    </span>
  );
}

/** A 28px icon button for row actions (Log submission, Edit, Chase). */
export const IconAction = forwardRef(function IconAction({ icon: Icon, label, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cn('grid h-7 w-7 shrink-0 place-items-center rounded-lg text-ink-3 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40', className)}
      {...props}
    >
      <Icon className="h-[15px] w-[15px]" aria-hidden="true" />
    </button>
  );
});

/** Row actions that show on hover / keyboard focus (always on touch screens). */
export function HoverActions({ children, className }) {
  return (
    <div className={cn('flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100 [@media(hover:none)]:opacity-100', className)}>
      {children}
    </div>
  );
}

/** List | Matrix (13 #10) and List | Board (14 #9): a small segmented switch with icons. */
export function ViewSwitch({ value, onChange, options, ariaLabel, testId, compact = false }) {
  return (
    <Segmented
      ariaLabel={ariaLabel}
      value={value}
      onChange={onChange}
      size="sm"
      testId={testId}
      options={options.map((option) => ({
        key: option.key,
        testId: `${testId}-${option.key}`,
        label: (
          <span className={cn('inline-flex items-center', compact ? 'gap-1' : 'gap-1.5')}>
            <option.icon className="h-3.5 w-3.5" aria-hidden="true" />
            {option.label}
          </span>
        ),
      }))}
    />
  );
}

/** '86 d' with its 'since 6 Jul' line. */
export function AgeCell({ days, sub }) {
  return (
    <span className="block leading-tight">
      <AgeText days={days} className="text-[13px]" />
      {sub ? <span className={subClass}>{sub}</span> : null}
    </span>
  );
}
